import * as THREE from "three";
import type { ArmorTier } from "../world/items";

/**
 * Procedural pixel-art texture generator for 3D worn armor sets:
 * Leather, Iron, and Diamond.
 *
 * Produces crisp NearestFilter textures with authentic Minecraft palettes,
 * highlights, seams, buckles, and cutouts for the helmet eye slit, neck opening,
 * and boots.
 */

export interface ArmorPieceTextures {
  readonly helmet: THREE.MeshBasicMaterial[];
  readonly chestTorso: THREE.MeshBasicMaterial[];
  readonly chestSleeve: THREE.MeshBasicMaterial[];
  readonly leggingsPelvis: THREE.MeshBasicMaterial[];
  readonly leggingsLeg: THREE.MeshBasicMaterial[];
  readonly boots: THREE.MeshBasicMaterial[];
}

function makeTexture(
  w: number,
  h: number,
  rows: readonly string[],
  palette: Readonly<Record<string, string>>,
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, w, h);
    for (let y = 0; y < h; y++) {
      const row = rows[y] || "";
      for (let x = 0; x < w; x++) {
        const ch = row[x];
        if (ch && palette[ch]) {
          ctx.fillStyle = palette[ch];
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  return texture;
}

const LEATHER_PALETTE: Readonly<Record<string, string>> = {
  "0": "#3F220E", // Dark seam
  "1": "#5E3619", // Dark shadow leather
  "2": "#7D4A24", // Mid shadow
  "3": "#8F562B", // Base leather
  "4": "#A86837", // Leather highlight
  "W": "#C27E47", // Peak highlight
  "S": "#404040", // Iron buckle/rivet
};

const IRON_PALETTE: Readonly<Record<string, string>> = {
  "0": "#7A7A7A", // Dark border
  "1": "#9E9E9E", // Shadow iron
  "2": "#B8B8B8", // Mid iron
  "3": "#D0D0D0", // Base iron
  "4": "#E4E4E4", // Light iron
  "W": "#FFFFFF", // Specular gleam
  "S": "#B0B0B0", // Rivet
};

const DIAMOND_PALETTE: Readonly<Record<string, string>> = {
  "0": "#115247", // Dark border
  "1": "#187D6D", // Deep cyan shadow
  "2": "#21A38F", // Mid cyan
  "3": "#2BC7AC", // Base diamond cyan
  "4": "#38EFE0", // Bright cyan
  "W": "#FFFFFF", // Specular shine
  "S": "#1C8F7C", // Rivet
};

const PALETTES: Record<ArmorTier, Readonly<Record<string, string>>> = {
  leather: LEATHER_PALETTE,
  iron: IRON_PALETTE,
  diamond: DIAMOND_PALETTE,
};

function createArmorTierTextures(tier: ArmorTier): ArmorPieceTextures {
  const pal = PALETTES[tier];

  const createMat = (
    w: number,
    h: number,
    rows: readonly string[],
    transparent = true,
  ): THREE.MeshBasicMaterial => {
    const tex = makeTexture(w, h, rows, pal);
    return new THREE.MeshBasicMaterial({
      map: tex,
      transparent,
      alphaTest: 0.1,
      fog: true,
      side: THREE.DoubleSide,
    });
  };

  // 1. Helmet (8x8 faces)
  const helmetFront = createMat(8, 8, [
    "43333332",
    "3W433341",
    "33343321",
    "2  43  1",
    "1  43  0",
    "        ",
    "        ",
    "        ",
  ]);
  const helmetSide = createMat(8, 8, [
    "43333332",
    "3W433341",
    "33333321",
    "33333321",
    "23333310",
    "1233210 ",
    "        ",
    "        ",
  ]);
  const helmetBack = createMat(8, 8, [
    "43333332",
    "3W433341",
    "33333321",
    "33333321",
    "33333321",
    "23333310",
    "12332100",
    "        ",
  ]);
  const helmetTop = createMat(8, 8, [
    "43333332",
    "34333321",
    "33W43321",
    "33443321",
    "33333321",
    "33333321",
    "23333310",
    "12222100",
  ]);
  const emptyMat = createMat(1, 1, [" "]);

  // BoxGeometry face order: +X, -X, +Y, -Y, +Z, -Z
  const helmet = [
    helmetSide, // 0: +X
    helmetSide, // 1: -X
    helmetTop,  // 2: +Y
    emptyMat,   // 3: -Y
    helmetBack, // 4: +Z
    helmetFront,// 5: -Z
  ];

  // 2. Chestplate Torso
  const chestFront = createMat(8, 12, [
    "33    32",
    "3W3  342",
    "43333331",
    "4W433431",
    "33333321",
    "33333321",
    "23333310",
    "23333310",
    "12333210",
    "12333210",
    "01222100",
    "00111000",
  ]);
  const chestBack = createMat(8, 12, [
    "33    32",
    "3W3  342",
    "43333331",
    "4W433431",
    "33333321",
    "33333321",
    "23333310",
    "23333310",
    "12333210",
    "12333210",
    "01222100",
    "00111000",
  ]);
  const chestSide = createMat(4, 12, [
    "    ",
    "    ",
    "4331",
    "4331",
    "3321",
    "3321",
    "2310",
    "2310",
    "1210",
    "1210",
    "0100",
    "0000",
  ]);
  const chestTop = createMat(8, 4, [
    "43333332",
    "43    32",
    "43    32",
    "32222210",
  ]);
  const chestTorso = [
    chestSide,  // +X
    chestSide,  // -X
    chestTop,   // +Y
    emptyMat,   // -Y
    chestBack,  // +Z
    chestFront, // -Z
  ];

  // Chestplate Sleeve
  const sleeveSide = createMat(4, 8, [
    "4332",
    "4W31",
    "3321",
    "3321",
    "2310",
    "1200",
    "0100",
    "0000",
  ]);
  const sleeveTop = createMat(4, 4, [
    "4332",
    "4W31",
    "3321",
    "2100",
  ]);
  const chestSleeve = [
    sleeveSide, // +X
    sleeveSide, // -X
    sleeveTop,  // +Y
    emptyMat,   // -Y
    sleeveSide, // +Z
    sleeveSide, // -Z
  ];

  // 3. Leggings Pelvis & Thigh
  const pelvisFront = createMat(8, 3, [
    "43333332",
    "3W3S3321",
    "23333310",
  ]);
  const pelvisSide = createMat(4, 3, [
    "4332",
    "3321",
    "2310",
  ]);
  const leggingsPelvis = [
    pelvisSide,  // +X
    pelvisSide,  // -X
    emptyMat,    // +Y
    emptyMat,    // -Y
    pelvisFront, // +Z
    pelvisFront, // -Z
  ];

  const thighSide = createMat(4, 8, [
    "4332",
    "4W31",
    "3321",
    "3321",
    "2310",
    "2310",
    "1200",
    "0100",
  ]);
  const leggingsLeg = [
    thighSide, // +X
    thighSide, // -X
    emptyMat,  // +Y
    emptyMat,  // -Y
    thighSide, // +Z
    thighSide, // -Z
  ];

  // 4. Boots
  const bootSide = createMat(4, 6, [
    "4332",
    "4W31",
    "3321",
    "3321",
    "2310",
    "0100",
  ]);
  const bootBottom = createMat(4, 4, [
    "0110",
    "1221",
    "1221",
    "0110",
  ]);
  const boots = [
    bootSide,   // +X
    bootSide,   // -X
    emptyMat,   // +Y
    bootBottom, // -Y
    bootSide,   // +Z
    bootSide,   // -Z
  ];

  return {
    helmet,
    chestTorso,
    chestSleeve,
    leggingsPelvis,
    leggingsLeg,
    boots,
  };
}

export class ArmorSetRegistry {
  private readonly sets: Record<ArmorTier, ArmorPieceTextures>;

  constructor() {
    this.sets = {
      leather: createArmorTierTextures("leather"),
      iron: createArmorTierTextures("iron"),
      diamond: createArmorTierTextures("diamond"),
    };
  }

  get(tier: ArmorTier): ArmorPieceTextures {
    return this.sets[tier];
  }

  allMaterials(): THREE.MeshBasicMaterial[] {
    const list: THREE.MeshBasicMaterial[] = [];
    for (const tier of ["leather", "iron", "diamond"] as const) {
      const set = this.sets[tier];
      list.push(
        ...set.helmet,
        ...set.chestTorso,
        ...set.chestSleeve,
        ...set.leggingsPelvis,
        ...set.leggingsLeg,
        ...set.boots,
      );
    }
    return list;
  }

  dispose(): void {
    for (const m of this.allMaterials()) {
      m.map?.dispose();
      m.dispose();
    }
  }
}
